import React, { useEffect, useState } from 'react';
import useSWR from 'swr';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  Alert, Box, Button, Card, CardContent, CardHeader, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Grid, LinearProgress, Rating, Snackbar, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { getApiBaseUrl, fetcherRilis } from '../rilis/PenggunaAplikasi';

const LABEL_JENIS = { masalah: 'Masalah', saran: 'Saran', lainnya: 'Lainnya', rating: 'Dari rating' };
const LABEL_STATUS = { baru: 'Baru', diproses: 'Diproses', selesai: 'Selesai' };
const WARNA_STATUS = { baru: 'warning', diproses: 'info', selesai: 'success' };
const waktu = (iso) => (iso ? new Date(iso).toLocaleString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-');
const pesanGalat = (e) => e?.response?.data?.message || e?.message || 'Gagal';

/** Gambar masukan butuh login: dimuat sebagai blob lewat sesi, bukan <img src> publik. */
const GambarMasukan = ({ nama, ukuran = 96 }) => {
  const [url, setUrl] = useState(null);
  const [buka, setBuka] = useState(false);
  const [galat, setGalat] = useState(false);
  useEffect(() => {
    let alamat = null;
    let batal = false;
    axios.get(`${getApiBaseUrl()}/masukan/gambar/${nama}`, { withCredentials: true, responseType: 'blob' })
      .then((r) => { if (!batal) { alamat = URL.createObjectURL(r.data); setUrl(alamat); } })
      .catch(() => !batal && setGalat(true));
    return () => { batal = true; if (alamat) URL.revokeObjectURL(alamat); };
  }, [nama]);
  const kotak = { width: ukuran, height: ukuran, borderRadius: 1, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center' };
  if (!url) return <Box sx={kotak}><Typography variant="caption" color="text.secondary">{galat ? 'Gagal' : '…'}</Typography></Box>;
  return (
    <>
      <Box component="img" src={url} alt="" onClick={() => setBuka(true)} sx={{ ...kotak, objectFit: 'cover', cursor: 'zoom-in' }} />
      <Dialog open={buka} onClose={() => setBuka(false)} maxWidth="lg">
        <Box component="img" src={url} alt="" onClick={() => setBuka(false)} sx={{ maxWidth: '100%', maxHeight: '90vh', display: 'block' }} />
      </Dialog>
    </>
  );
};

// ------------------------------------------------------------------ Detail tiket

const DetailTiket = ({ uuid, onTutup, onBerubah }) => {
  const base = getApiBaseUrl();
  const { data, error, mutate } = useSWR(uuid ? `${base}/masukan/${uuid}` : null, fetcherRilis);
  const m = data?.data;
  const [status, setStatus] = useState('baru');
  const [balasan, setBalasan] = useState('');
  const [menyimpan, setMenyimpan] = useState(false);
  const [galat, setGalat] = useState('');

  useEffect(() => {
    if (m) { setStatus(m.status); setBalasan(m.balasan || ''); }
  }, [m]);

  const simpan = async () => {
    setMenyimpan(true); setGalat('');
    try {
      await axios.put(`${base}/masukan/${uuid}`, { status, balasan }, { withCredentials: true });
      await mutate();
      onBerubah('Tersimpan' + (balasan.trim() ? '; balasan terlihat di aplikasi pengirim' : ''));
    } catch (e) {
      setGalat(pesanGalat(e));
    } finally {
      setMenyimpan(false);
    }
  };

  const hapus = async () => {
    const ok = await Swal.fire({ title: 'Hapus masukan?', text: 'Masukan ini dan gambarnya akan dihapus.', icon: 'warning', showCancelButton: true, confirmButtonText: 'Hapus', cancelButtonText: 'Batal', confirmButtonColor: '#d33' });
    if (!ok.isConfirmed) return;
    try {
      await axios.delete(`${base}/masukan/${uuid}`, { withCredentials: true });
      onBerubah('Masukan dihapus');
      onTutup();
    } catch (e) {
      Swal.fire('Gagal', pesanGalat(e), 'error');
    }
  };

  return (
    <Dialog open={!!uuid} onClose={() => !menyimpan && onTutup()} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {m ? LABEL_JENIS[m.jenis] : 'Masukan'}
        {m && <Chip size="small" color={WARNA_STATUS[m.status]} label={LABEL_STATUS[m.status]} />}
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error">{pesanGalat(error)}</Alert>}
        {!m && !error && <LinearProgress />}
        {m && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography sx={{ whiteSpace: 'pre-line' }}>{m.isi}</Typography>
            {m.gambar.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>{m.gambar.map((g) => <GambarMasukan key={g} nama={g} />)}</Box>
            )}
            <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
              <Typography variant="body2"><b>{m.pengirim?.nama || '-'}</b> ({m.pengirim?.username} · {m.pengirim?.role || m.peran})</Typography>
              <Typography variant="body2">{m.cabang?.namacabang || 'Tanpa cabang'} · dikirim {waktu(m.createdAt)}</Typography>
              <Typography variant="body2" color="text.secondary">
                Versi aplikasi {m.versi ? `${m.versi} (build ${m.build})` : '-'} · {m.perangkat || '-'}
              </Typography>
            </Box>
            <ToggleButtonGroup exclusive size="small" value={status} onChange={(_, v) => v && setStatus(v)} disabled={menyimpan}>
              {Object.entries(LABEL_STATUS).map(([k, v]) => <ToggleButton key={k} value={k}>{v}</ToggleButton>)}
            </ToggleButtonGroup>
            <TextField
              label="Balasan untuk pengirim" multiline minRows={3} value={balasan} onChange={(e) => setBalasan(e.target.value)}
              helperText={m.dibalasAt ? `Dibalas ${waktu(m.dibalasAt)} · terlihat di aplikasi pengirim (Tentang Aplikasi › Masukan saya)` : 'Terlihat di aplikasi pengirim (Tentang Aplikasi › Masukan saya)'}
              inputProps={{ maxLength: 3000 }} disabled={menyimpan}
            />
            {galat && <Alert severity="error">{galat}</Alert>}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {m && <Button color="error" onClick={hapus} disabled={menyimpan} sx={{ mr: 'auto' }}>Hapus</Button>}
        <Button onClick={onTutup} disabled={menyimpan}>Tutup</Button>
        <Button variant="contained" onClick={simpan} disabled={!m || menyimpan}>Simpan</Button>
      </DialogActions>
    </Dialog>
  );
};

// ------------------------------------------------------------------ Tab Tiket

const TabTiket = ({ bukaTiket, kunciSegar }) => {
  const base = getApiBaseUrl();
  const [status, setStatus] = useState('baru');
  const [jenis, setJenis] = useState('');
  const [cari, setCari] = useState('');
  const params = new URLSearchParams({ ...(status !== 'semua' && { status }), ...(jenis && { jenis }), ...(cari.trim() && { q: cari.trim() }) });
  const { data, error, isLoading, mutate } = useSWR(`${base}/masukan?${params}`, fetcherRilis);
  // Muat ulang setelah tiket diubah/dihapus di jendela detail (bukan saat pertama tampil).
  useEffect(() => { if (kunciSegar > 0) mutate(); }, [kunciSegar, mutate]);
  const r = data?.ringkasan || {};
  const list = data?.data || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
        {['baru', 'diproses', 'selesai', 'semua'].map((s) => (
          <Chip key={s} clickable color={status === s ? 'primary' : 'default'} onClick={() => setStatus(s)}
            label={s === 'semua' ? 'Semua' : `${LABEL_STATUS[s]} (${r[s] ?? 0})`} />
        ))}
        <TextField select size="small" label="Jenis" value={jenis} onChange={(e) => setJenis(e.target.value)} SelectProps={{ native: true }} sx={{ minWidth: 140 }} InputLabelProps={{ shrink: true }}>
          <option value="">Semua</option>
          {Object.entries(LABEL_JENIS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </TextField>
        <TextField size="small" label="Cari isi" value={cari} onChange={(e) => setCari(e.target.value)} />
      </Box>
      {error && <Alert severity="error">{pesanGalat(error)}</Alert>}
      {isLoading && <LinearProgress />}
      {data && !list.length && <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Tidak ada masukan.</Typography>}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {list.map((m) => (
          <Card key={m.uuid} variant="outlined" sx={{ cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }} onClick={() => bukaTiket(m.uuid)}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                <Typography fontWeight={700}>{LABEL_JENIS[m.jenis]}</Typography>
                <Chip size="small" color={WARNA_STATUS[m.status]} label={LABEL_STATUS[m.status]} />
                {m.gambar.length > 0 && <Chip size="small" variant="outlined" label={`${m.gambar.length} gambar`} />}
                {m.balasan && <Chip size="small" variant="outlined" label="Dibalas" />}
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>{waktu(m.createdAt)}</Typography>
              </Box>
              <Typography variant="body2" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{m.isi}</Typography>
              <Typography variant="caption" color="text.secondary">
                {m.pengirim?.nama || '-'} · {m.pengirim?.role || m.peran} · {m.cabang?.namacabang || 'Tanpa cabang'}{m.versi ? ` · v${m.versi}` : ''}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

// ------------------------------------------------------------------ Tab Rating

const TabRating = ({ bukaTiket, setPesan }) => {
  const base = getApiBaseUrl();
  const { data, error, isLoading, mutate } = useSWR(`${base}/rating`, fetcherRilis);
  const [jarak, setJarak] = useState('');
  useEffect(() => { if (data) setJarak(String(data.intervalHari ?? 30)); }, [data]);
  const r = data?.ringkasan || {};
  const jumlahAkun = r.jumlahAkun || 0;

  const simpanInterval = async () => {
    try {
      const x = await axios.put(`${base}/rating/pengaturan`, { intervalHari: Number(jarak) }, { withCredentials: true });
      setPesan(x.data?.message || 'Tersimpan');
      mutate();
    } catch (e) {
      Swal.fire('Gagal', pesanGalat(e), 'error');
    }
  };

  const tanyaSekarang = async () => {
    const ok = await Swal.fire({
      title: 'Tanya semua pengguna sekarang?',
      text: 'Jendela rating muncul di HP semua pengguna aplikasi (admin cabang, kasir, karyawan, dapur) saat aplikasi dibuka berikutnya, tanpa menunggu jarak hari.',
      icon: 'question', showCancelButton: true, confirmButtonText: 'Ya, tanya', cancelButtonText: 'Batal',
    });
    if (!ok.isConfirmed) return;
    try {
      const x = await axios.post(`${base}/rating/tanya-sekarang`, {}, { withCredentials: true });
      setPesan(x.data?.message || 'Tersimpan');
    } catch (e) {
      Swal.fire('Gagal', pesanGalat(e), 'error');
    }
  };

  if (error) return <Alert severity="error">{pesanGalat(error)}</Alert>;
  if (isLoading || !data) return <LinearProgress />;
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={5}>
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardHeader title="Ringkasan" subheader="Dari nilai terakhir tiap akun" />
          <CardContent sx={{ pt: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="h3" fontWeight={700}>{r.rataRata ?? '-'}</Typography>
              <Box>
                <Rating value={r.rataRata || 0} precision={0.1} readOnly />
                <Typography variant="body2" color="text.secondary">{jumlahAkun} akun · {r.jumlahPenilaian || 0} penilaian</Typography>
              </Box>
            </Box>
            {[5, 4, 3, 2, 1].map((b) => (
              <Box key={b} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ width: 28 }}>{b} ★</Typography>
                <LinearProgress variant="determinate" value={jumlahAkun ? ((r.sebaran?.[b] || 0) / jumlahAkun) * 100 : 0}
                  sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: '#e5e7eb', '& .MuiLinearProgress-bar': { bgcolor: '#f59e0b' } }} />
                <Typography variant="body2" sx={{ width: 28, textAlign: 'right' }}>{r.sebaran?.[b] || 0}</Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardHeader title="Jendela rating otomatis" subheader="Semua pengguna aplikasi (kecuali superadmin) ditanya nilai aplikasi berkala, saat aplikasi baru dibuka (tidak di tengah transaksi). Isi 0 untuk mematikan." />
          <CardContent sx={{ pt: 0, display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <TextField size="small" type="number" label="Setiap (hari)" value={jarak} onChange={(e) => setJarak(e.target.value)} sx={{ width: 140 }} inputProps={{ min: 0, max: 365 }} />
            <Button variant="contained" onClick={simpanInterval}>Simpan</Button>
          </CardContent>
          <CardContent sx={{ pt: 0 }}>
            <Button variant="outlined" onClick={tanyaSekarang}>Tanya semua pengguna sekarang</Button>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
              Untuk mencoba, atau meminta nilai setelah rilis besar. Muncul saat aplikasi dibuka berikutnya.
            </Typography>
          </CardContent>
        </Card>
        {data.perVersi.length > 0 && (
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader title="Per versi" />
            <CardContent sx={{ pt: 0 }}>
              {data.perVersi.map((v) => (
                <Box key={v.versi} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="body2">Versi {v.versi} <Typography component="span" variant="caption" color="text.secondary">({v.jumlah} penilaian)</Typography></Typography>
                  <Typography variant="body2" fontWeight={600}>{v.rataRata} ★</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}
        {data.perCabang.length > 0 && (
          <Card variant="outlined">
            <CardHeader title="Per cabang" />
            <CardContent sx={{ pt: 0 }}>
              {data.perCabang.map((c) => (
                <Box key={c.cabanguuid || '-'} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="body2">{c.namacabang} <Typography component="span" variant="caption" color="text.secondary">({c.jumlah} akun)</Typography></Typography>
                  <Typography variant="body2" fontWeight={600}>{c.rataRata} ★</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}
      </Grid>
      <Grid item xs={12} md={7}>
        <Card variant="outlined">
          <CardHeader title="Penilaian terbaru" />
          <CardContent sx={{ pt: 0 }}>
            {!data.data.length && <Typography color="text.secondary">Belum ada penilaian.</Typography>}
            {data.data.map((x) => (
              <Box key={x.uuid} sx={{ py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Rating value={x.nilai} readOnly size="small" />
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>{waktu(x.createdAt)}</Typography>
                </Box>
                {x.komentar && <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mt: 0.5 }}>{x.komentar}</Typography>}
                <Typography variant="caption" color="text.secondary" component="div">
                  {x.pengirim?.nama || '-'} · {x.cabang?.namacabang || 'Tanpa cabang'}{x.versi ? ` · v${x.versi}` : ''}
                </Typography>
                {x.masukanuuid && <Button size="small" sx={{ px: 0 }} onClick={() => bukaTiket(x.masukanuuid)}>Lihat tiketnya</Button>}
              </Box>
            ))}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

// ------------------------------------------------------------------ Halaman

const Kepala = ({ judul, keterangan }) => (
  <>
    <Typography variant="h5" fontWeight={700}>{judul}</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{keterangan}</Typography>
  </>
);

const Bingkai = ({ children }) => <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#f4f6f8', minHeight: '100vh' }}>{children}</Box>;

/** Superadmin: masukan (tiket) dari aplikasi. Isi sama dengan aplikasi (Pengaturan › Masukan). */
export const MasukanTiket = () => {
  const [tiket, setTiket] = useState(null);
  const [pesan, setPesan] = useState('');
  const [kunciSegar, setKunciSegar] = useState(0);
  return (
    <Bingkai>
      <Kepala judul="Masukan" keterangan="Masukan dan laporan masalah dari pengguna aplikasi (Tentang Aplikasi › Kirim masukan). Balasan terlihat di aplikasi pengirim." />
      <TabTiket bukaTiket={setTiket} kunciSegar={kunciSegar} />
      {tiket && <DetailTiket uuid={tiket} onTutup={() => setTiket(null)} onBerubah={(p) => { setPesan(p); setKunciSegar((k) => k + 1); }} />}
      <Snackbar open={!!pesan} autoHideDuration={2500} onClose={() => setPesan('')} message={pesan} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Bingkai>
  );
};

/** Superadmin: rating aplikasi + pengaturan jendela rating otomatis. Isi sama dengan aplikasi (Pengaturan › Rating Aplikasi). */
export const RatingAplikasiHalaman = () => {
  const [tiket, setTiket] = useState(null);
  const [pesan, setPesan] = useState('');
  return (
    <Bingkai>
      <Kepala judul="Rating Aplikasi" keterangan="Nilai 1–5 bintang dari pengguna aplikasi (Tentang Aplikasi › Beri nilai, dan jendela otomatis berkala untuk semua peran)." />
      <TabRating bukaTiket={setTiket} setPesan={setPesan} />
      {tiket && <DetailTiket uuid={tiket} onTutup={() => setTiket(null)} onBerubah={setPesan} />}
      <Snackbar open={!!pesan} autoHideDuration={2500} onClose={() => setPesan('')} message={pesan} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Bingkai>
  );
};
